import { useCallback } from "react";

import * as Repository from "@/components/features/design/plain/model/repository";
import { notifyUserEventInfo } from "@/components/features/side-effect/notify-user-event-info";
import { useLoadingScreen } from "@/components/features/side-effect/show-loading-screen-to-user/_hooks/use-loading-screen";

import { toGuestPageVisibilitySettingsVariables } from "@/components/features-new/guest-page-visibility";

import type * as FormModelType from "../../type";

export const useUpdatePlainSettingAndWidget = (
	inputModel: FormModelType.PlainSettingFormModel["InputModel"],
	/** 取得した公開設定（プレーン以外のページはこの値のまま送る） */
	guestPageVisibilitySettings: FormModelType.QueryData["guestPageVisibilitySettings"],
	onResetViewModel: () => void,
) => {
	const { showLoadingScreenCallback, hideLoadingScreenCallback } = useLoadingScreen();
	const { onUpdatePlainSetting } = Repository.Mutation.usePlainSettingMutation();
	const { onUpdatePlainWidget } = Repository.Mutation.usePlainWidgetMutation();
	/**
	 * @todo 副作用を UI で定義
	 * プレーン・ウィジェットの更新
	 */
	const onUpdatePlainSettingAndWidget = useCallback(() => {
		showLoadingScreenCallback();

		const resolvedPromiseArray = [
			onUpdatePlainSetting(
				{
					backgroundImage: inputModel.backgroundImage,
					backgroundType: inputModel.backgroundType,
					colorBackground: inputModel.colorBackground,
					colorFont: inputModel.colorFont,
					colorPrimary: inputModel.colorPrimary,
					domainName: inputModel.domainName,
					fontFamily: inputModel.fontFamily,
					gtmID: inputModel.gtmID,
					hotelID: inputModel.hotelID,
					isShowHotelCatchCopy: inputModel.isShowHotelCatchCopy,
					isShowHotelKeyVisual: inputModel.isShowHotelKeyVisual,
					isShowHotelLogo: inputModel.isShowHotelLogo,
					isShowHotelName: inputModel.isShowHotelName,
					isShowMagazineAbout: inputModel.isShowMagazineAbout,
					isShowMagazinePlan: inputModel.isShowMagazinePlan,
					isShowMagazineRoom: inputModel.isShowMagazineRoom,
					logoImage: inputModel.logoImage,
					logoSize: inputModel.logoSize,
					reservationComponentType: inputModel.reservationComponentType,
					reservationComponentViewerOrder: inputModel.reservationComponentViewerOrder,
					widgetDecoration: inputModel.widgetDecoration,
				},
				toGuestPageVisibilitySettingsVariables({
					saved: guestPageVisibilitySettings,
					visibilityKey: "plainVisibility",
					visibility: inputModel.guestPageVisibility,
				}),
			),
			onUpdatePlainWidget(inputModel.plainWidgetDefault),
		];
		Promise.all(resolvedPromiseArray)
			.then((result) => {
				const isNotError = result.every((r) => !r.errors);

				if (isNotError) {
					notifyUserEventInfo({
						variant: "default",
						title: "プレーンの設定を更新しました",
					});
					onResetViewModel();
				}
			})
			.finally(hideLoadingScreenCallback);
	}, [
		hideLoadingScreenCallback,
		inputModel.backgroundImage,
		inputModel.backgroundType,
		inputModel.colorBackground,
		inputModel.colorFont,
		inputModel.colorPrimary,
		inputModel.domainName,
		inputModel.fontFamily,
		inputModel.gtmID,
		inputModel.guestPageVisibility,
		guestPageVisibilitySettings,
		inputModel.hotelID,
		inputModel.isShowHotelCatchCopy,
		inputModel.isShowHotelKeyVisual,
		inputModel.isShowHotelLogo,
		inputModel.isShowHotelName,
		inputModel.isShowMagazineAbout,
		inputModel.isShowMagazinePlan,
		inputModel.isShowMagazineRoom,
		inputModel.logoImage,
		inputModel.logoSize,
		inputModel.plainWidgetDefault,
		inputModel.reservationComponentType,
		inputModel.reservationComponentViewerOrder,
		inputModel.widgetDecoration,
		onResetViewModel,
		onUpdatePlainSetting,
		onUpdatePlainWidget,
		showLoadingScreenCallback,
	]);

	return {
		onUpdatePlainSettingAndWidget,
	};
};
